import { useEffect, useState } from "react";
import moment from "moment";
import { TrendingUp } from "lucide-react";

/**
 * The lead assistant's real, measured performance: of the patient messages
 * it was asked to answer, how many it handled itself (including safety
 * handovers like "call 108") versus didn't understand. Weekly bars for the
 * last 8 weeks. `load()` returns the /leads/assistant-stats payload.
 */
export default function AssistantStats({ load, reloadKey }) {
  const [stats, setStats] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    load().then(setStats).catch(() => setFailed(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  if (failed) return <p className="text-sm text-muted-foreground">Couldn't load the assistant's performance.</p>;
  if (!stats) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const total = stats.handled + stats.not_understood;
  const withData = stats.weeks.filter((w) => w.accuracy != null);
  const first = withData[0]?.accuracy;
  const last = withData.at(-1)?.accuracy;

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-4">
        <Tile label="Handled automatically" value={stats.accuracy == null ? "—" : `${stats.accuracy}%`} hint={total ? `${stats.handled} of ${total} messages` : "No messages yet"} strong />
        <Tile label="Sent to staff (not understood)" value={stats.not_understood} hint="last 8 weeks" />
        <Tile label="Bookings requested" value={stats.bookings_requested} hint={`${stats.conversations} chats`} />
        <Tile label="Learned from real chats" value={stats.learned_phrasings} hint={`${stats.learned_answers} answers`} />
      </div>
      {withData.length > 0 && (
        <div>
          <div className="flex h-24 items-end gap-1.5" role="img" aria-label="Weekly share of messages handled automatically">
            {stats.weeks.map((w) => (
              <div key={w.week_start} className="flex flex-1 flex-col items-center gap-1">
                <span className="text-[10px] tabular-nums text-muted-foreground">{w.accuracy == null ? "" : `${w.accuracy}%`}</span>
                <div
                  className={w.accuracy == null ? "w-full rounded-sm bg-muted" : "w-full rounded-sm bg-primary"}
                  style={{ height: `${Math.max(w.accuracy ?? 4, 4) * 0.6}px` }}
                  title={`${moment(w.week_start).format("D MMM")}: ${w.handled} handled, ${w.not_understood} not understood`}
                />
                <span className="text-[10px] text-muted-foreground">{moment(w.week_start).format("D MMM")}</span>
              </div>
            ))}
          </div>
          {first != null && last != null && withData.length > 1 && (
            <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
              <TrendingUp className="h-3.5 w-3.5" /> {first}% → {last}% handled automatically, week by week
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, hint, strong }) {
  return (
    <div className="rounded-md border border-border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={strong ? "text-2xl font-semibold tabular-nums" : "text-xl font-semibold tabular-nums"}>{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
